import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, USER_ROLES } from "../auth/AuthContext";
import styled, { keyframes, css } from "styled-components";
import { FaEnvelope, FaWrench, FaRecycle, FaShoppingBag, FaCar, FaTruck, FaStoreAlt, FaTools, FaBuilding, FaCogs } from "react-icons/fa";
import {
  saveSignupSelection,
  clearSignupSelection,
  loadSignupSelection,
  hasRoleSelected,
  isSignupSelectionComplete,
  NEW_SELLER_CATEGORIES,
  USED_SELLER_CATEGORIES,
} from "./auth/SignUpAccountSelect";
import PartnerSignupHero from "./auth/PartnerSignupHero";
import PartnerBusinessForm from "./auth/PartnerBusinessForm";
import {
  normalizePhone,
  getIndianMobileError,
} from "../utils/phoneValidation";
import {
  saveBusinessDraft,
  clearBusinessDraft,
  saveSignupStep,
  loadSignupStep,
  loadBusinessDraft,
  markSignupSessionActive,
  hasActiveSignupSession,
  scrollSignupPanelToTop,
  uploadPartnerFile,
  hasPartnerFileField,
  buildPartnerOnboardingPayload,
  focusSignupField,
  GARAGE_COMPANY_TYPES,
  EMPTY_BUSINESS_FORM,
} from "./auth/partnerSignupBusiness";
import { getDefaultClustersForTier } from "../constants/garageServiceClusters";
import { authService } from "../services/apiService";
import {
  fadeIn,
  fadeInUp,
  fadeInDown,
  scaleIn,
  slideInForward,
  slideInBack,
  popIn,
  shake,
  arrowNudge,
  reducedMotion,
} from "./auth/partnerSignupAnimations";
import { partnerSignupThemeVars } from "./auth/partnerSignupTheme";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ONBOARDING_DRAFT_KEYS = [
  'onboarding_vendor_draft',
  'onboarding_garage_draft',
  'onboarding_mechanic_draft',
  'onboarding_delivery_draft',
  'onboarding_customer_draft',
];

const SELLER_ROLES = NEW_SELLER_CATEGORIES.map(({ id, label, desc, icon }) => ({ id, label, desc, icon }));

const GARAGE_ROLE_ICONS = {
  small_garage: FaTools,
  medium_garage: FaCar,
  large_garage: FaBuilding,
  custom_garage: FaStoreAlt,
  transformer: FaCogs,
  transformal: FaCogs,
};

const USED_SELLER_ROLE_ICONS = {
  medium_scrapyard: FaCar,
  large_scrapyard: FaTruck,
  shop_sparepart: FaStoreAlt,
};

function isRoleCardSelected(id, selection) {
  return (
    selection?.mainType === 'sparepart_seller' &&
    selection?.partCondition === 'new' &&
    selection?.sellerCategory === id
  );
}

function isGarageCardSelected(id, selection) {
  return selection?.mainType === 'garage' && selection?.sellerCategory === id;
}

function isUsedSellerCardSelected(id, selection) {
  return (
    selection?.mainType === 'sparepart_seller' &&
    selection?.partCondition === 'used' &&
    selection?.sellerCategory === id
  );
}

const clearFreshRegistrationSession = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
  localStorage.removeItem('userRole');
  localStorage.removeItem('vendor_profile');
  clearSignupSelection();
  clearBusinessDraft();
  ONBOARDING_DRAFT_KEYS.forEach((key) => localStorage.removeItem(key));
};

const EMPTY_SELECTION = { mainType: null, partCondition: null, sellerCategory: null };

function getPostSignupDestination(selection) {
  if (selection?.mainType === 'garage') {
    return { role: USER_ROLES.GARAGE, path: '/onboarding/garage' };
  }
  if (selection?.mainType === 'sparepart_seller' && selection?.partCondition === 'used') {
    return { role: USER_ROLES.USED_PENDING, path: '/signup/pending' };
  }
  if (selection?.mainType === 'sparepart_seller' && selection?.partCondition === 'new') {
    return { role: USER_ROLES.VENDOR, path: '/onboarding/vendor' };
  }
  return { role: USER_ROLES.CUSTOMER, path: '/' };
}

function applySignupSelectionToUser(user, selection, updateUser) {
  const { role, path } = getPostSignupDestination(selection);
  if (role === 'pending') return '/';

  const updatedUser = {
    ...user,
    role,
    id: user?.id || user?._id,
    _id: user?._id || user?.id,
  };
  localStorage.setItem('user', JSON.stringify(updatedUser));
  localStorage.setItem('userRole', role);

  if (
    selection?.mainType === 'sparepart_seller' &&
    selection?.partCondition === 'new' &&
    selection?.sellerCategory
  ) {
    localStorage.setItem(
      'onboarding_vendor_draft',
      JSON.stringify({
        sellerCategory: selection.sellerCategory,
        partCondition: 'new',
      })
    );
  }

  if (updateUser) updateUser(updatedUser);
  return path;
}

const PhoneRow = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid ${(p) => (p.$hasError ? 'var(--pp-error-border)' : 'var(--pp-border)')};
  border-radius: var(--pp-radius-btn);
  overflow: hidden;
  background: var(--pp-surface);
`;

const PhonePrefix = styled.span`
  display: flex;
  align-items: center;
  padding: 0 12px;
  font-size: 14px;
  font-weight: 600;
  color: var(--pp-muted);
  background: var(--pp-input-muted-bg);
  border-right: 1px solid var(--pp-border);
  flex-shrink: 0;
`;

const PhoneInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 11px 12px;
  border: none;
  font-size: 14px;
  color: var(--pp-text);
  background: transparent;
  outline: none;
  box-sizing: border-box;
  caret-color: var(--pp-text);

  &::placeholder {
    color: var(--pp-muted);
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px var(--pp-surface) inset !important;
    box-shadow: 0 0 0 1000px var(--pp-surface) inset !important;
    -webkit-text-fill-color: var(--pp-text) !important;
    caret-color: var(--pp-text);
  }
`;

const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, logout, updateUser } = useAuth();

  const [signupStep, setSignupStep] = useState(1);
  const [stepDirection, setStepDirection] = useState('forward');
  const [accountSelection, setAccountSelection] = useState(EMPTY_SELECTION);
  const [businessData, setBusinessData] = useState(() => ({ ...EMPTY_BUSINESS_FORM }));
  const [formData, setFormData] = useState({ email: "", phone: "" });
  const [localError, setLocalError] = useState("");
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitPhase, setSubmitPhase] = useState('');
  const [fieldError, setFieldError] = useState({ id: null, message: '' });
  const leftPanelRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      await logout();
      if (cancelled) return;

      const savedSelection = loadSignupSelection();
      const canRestore =
        hasActiveSignupSession() &&
        savedSelection &&
        isSignupSelectionComplete(savedSelection);

      if (canRestore) {
        const savedStep = loadSignupStep();
        setSignupStep(savedStep >= 1 && savedStep <= 3 ? savedStep : 1);
        setAccountSelection(savedSelection);
        setBusinessData(loadBusinessDraft());
        return;
      }

      clearFreshRegistrationSession();
      markSignupSessionActive();
      setSignupStep(1);
      setAccountSelection(EMPTY_SELECTION);
      setBusinessData({ ...EMPTY_BUSINESS_FORM });
    })();

    return () => {
      cancelled = true;
    };
    // Run once on signup mount — do not reset progress when auth context updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const persistSignupProgress = () => {
      if (document.visibilityState !== 'hidden') return;
      try {
        saveSignupStep(signupStep);
        saveSignupSelection(accountSelection);
        saveBusinessDraft(businessData);
        markSignupSessionActive();
      } catch {
        // ignore
      }
    };
    document.addEventListener('visibilitychange', persistSignupProgress);
    return () => document.removeEventListener('visibilitychange', persistSignupProgress);
  }, [signupStep, accountSelection, businessData]);

  useEffect(() => {
    saveSignupStep(signupStep);
  }, [signupStep]);

  useEffect(() => {
    if (leftPanelRef.current) leftPanelRef.current.scrollTop = 0;
    scrollSignupPanelToTop('partner-signup-top');
  }, [signupStep]);

  const goToSignupStep = (step) => {
    setSignupStep(step);
    saveSignupStep(step);
  };

  const handleAccountSelectionChange = (next) => {
    setAccountSelection(next);
    saveSignupSelection(next);
    markSignupSessionActive();
    setLocalError("");
  };

  const selectMainType = (mainType) => {
    handleAccountSelectionChange({
      mainType,
      partCondition: null,
      sellerCategory: null,
    });
  };

  const selectPartCondition = (condition) => {
    handleAccountSelectionChange({
      mainType: 'sparepart_seller',
      partCondition: condition,
      sellerCategory: condition === 'new' ? accountSelection.sellerCategory : null,
    });
  };

  const selectGarageType = (garageTypeId) => {
    handleAccountSelectionChange({
      mainType: 'garage',
      partCondition: null,
      sellerCategory: garageTypeId,
    });
  };

  const selectUsedSellerType = (typeId) => {
    handleAccountSelectionChange({
      mainType: 'sparepart_seller',
      partCondition: 'used',
      sellerCategory: typeId,
    });
  };

  const selectRole = (roleId) => {
    handleAccountSelectionChange({
      mainType: 'sparepart_seller',
      partCondition: 'new',
      sellerCategory: roleId,
    });
  };

  const handleBackAccountType = () => {
    handleAccountSelectionChange(EMPTY_SELECTION);
    setLocalError("");
  };

  const handleBackStep1 = () => {
    setLocalError('');
    if (accountSelection.sellerCategory) {
      handleAccountSelectionChange({ ...accountSelection, sellerCategory: null });
      return;
    }
    if (accountSelection.partCondition) {
      handleAccountSelectionChange({ ...accountSelection, partCondition: null, sellerCategory: null });
      return;
    }
    if (accountSelection.mainType) {
      handleBackAccountType();
    }
  };

  const showStep1Back = Boolean(accountSelection.mainType);

  const handleContinueStep1 = () => {
    if (!hasRoleSelected(accountSelection)) {
      setLocalError("Please choose your role to continue");
      return;
    }
    setLocalError("");
    markSignupSessionActive();
    if (accountSelection.mainType === 'garage' && accountSelection.sellerCategory) {
      const sellerCategory = accountSelection.sellerCategory === 'transformal'
        ? 'transformer'
        : accountSelection.sellerCategory;
      const synced = {
        ...businessData,
        companyType: sellerCategory,
        selectedClusters: getDefaultClustersForTier(sellerCategory),
        fuelSpecialistTypes: [],
      };
      setBusinessData(synced);
      saveBusinessDraft(synced);
    }
    setStepDirection('forward');
    goToSignupStep(2);
  };

  const handleBusinessComplete = () => {
    setLocalError("");
    setStepDirection('forward');
    goToSignupStep(3);
  };

  const handleBackToStep1 = () => {
    setStepDirection('back');
    goToSignupStep(1);
    setLocalError("");
  };

  const handleBackToStep2 = () => {
    setStepDirection('back');
    goToSignupStep(2);
    setLocalError("");
  };

  const finalizePartnerRegistration = useCallback(async (user, contactEmail = '', contactPhone = '') => {
    const { role } = getPostSignupDestination(accountSelection);
    const safeUpload = async (field, label) => {
      if (!hasPartnerFileField(field)) return '';
      try {
        const url = await uploadPartnerFile(field);
        if (!url) {
          throw new Error(`Failed to upload ${label}. Please retake the photo and try again.`);
        }
        return url;
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || `Failed to upload ${label}`;
        throw new Error(msg);
      }
    };
    const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const emailForPending = (contactEmail || user?.email || '').trim();
    const phoneForPending = normalizePhone(contactPhone || user?.phone || '');

    const uploaded = {
      ...businessData,
      contactEmail: emailForPending,
      registrationCertificateUrl: await safeUpload(businessData.registrationCertificate, 'registration certificate'),
    };
    await pause(300);
    uploaded.panCardDocumentUrl = await safeUpload(businessData.panCardDocument, 'PAN card');
    await pause(300);
    uploaded.bankPassbookUrl = await safeUpload(businessData.bankPassbook, 'bank passbook');
    await pause(300);
    uploaded.aadhaarFrontUrl = await safeUpload(businessData.aadhaarFront, 'Aadhaar card');
    await pause(300);
    uploaded.shopPictureUrl = await safeUpload(businessData.shopPicture, 'shop picture');
    await pause(300);
    uploaded.streetPictureUrl = await safeUpload(businessData.streetPicture, 'street picture');
    saveBusinessDraft(uploaded);

    const payload = buildPartnerOnboardingPayload(uploaded, role, accountSelection.sellerCategory, accountSelection);
    if (user && updateUser) {
      try {
        const updated = await authService.completeOnboarding(payload);
        const partnerId = updated?.partnerId || updated?.vendorDetails?.partnerId || '';
        if (partnerId) {
          localStorage.setItem('partner_signup_partner_id', partnerId);
        }
        if (updated) {
          updateUser(updated);
        } else {
          updateUser({
            ...user,
            role: payload.role,
            onboardingComplete: false,
            partnerId: partnerId || user.partnerId,
            vendorDetails: payload.vendorDetails,
          });
        }
      } catch (onboardErr) {
        console.error('Partner onboarding save failed:', onboardErr);
        const msg = onboardErr?.response?.data?.message || onboardErr?.message || 'Could not save registration';
        throw new Error(msg);
      }
    }
    if (phoneForPending) localStorage.setItem('partner_signup_phone', phoneForPending);
    if (emailForPending) localStorage.setItem('partner_signup_email', emailForPending);
    localStorage.setItem('partner_signup_pending', 'true');
    clearBusinessDraft();
    navigate('/signup/pending', { replace: true });
  }, [accountSelection, businessData, navigate, updateUser]);

  const handleChange = (e) => {
    const { name, value, id } = e.target;
    const nextValue = name === 'phone' ? normalizePhone(value).slice(0, 10) : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    setLocalError("");
    setFieldError((prev) => {
      if (!prev.id) return prev;
      if (prev.id === id || prev.id === `signup-${name}`) return { id: null, message: '' };
      return prev;
    });
  };

  const handlePhoneChange = (e) => {
    const digits = normalizePhone(e.target.value).slice(0, 10);
    setFormData((prev) => ({ ...prev, phone: digits }));
    setLocalError("");
    if (fieldError.id === 'signup-phone') setFieldError({ id: null, message: '' });
  };

  const showAccountError = (message, fieldId) => {
    if (fieldId) {
      setFieldError({ id: fieldId, message });
      setLocalError('');
    } else {
      setLocalError(message);
      setFieldError({ id: null, message: '' });
    }
    focusSignupField(fieldId);
  };

  const fe = (id) => ({
    $hasError: fieldError.id === id,
    msg: fieldError.id === id ? fieldError.message : '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError("");
    setFieldError({ id: null, message: '' });

    if (!isSignupSelectionComplete(accountSelection)) {
      goToSignupStep(1);
      showAccountError("Please choose your role to continue", null);
      return;
    }

    const email = formData.email.trim();
    const phone = normalizePhone(formData.phone);

    if (!email) {
      showAccountError('Email address is required', 'signup-email');
      return;
    }
    if (!EMAIL_RE.test(email)) {
      showAccountError('Enter a valid email address', 'signup-email');
      return;
    }

    const phoneErr = getIndianMobileError(phone) || (!phone ? 'Mobile number is required' : '');
    if (phoneErr) {
      showAccountError(phoneErr, 'signup-phone');
      return;
    }

    if (!agreeToTerms) {
      showAccountError("Please agree to the Terms of Use", 'signup-terms');
      return;
    }

    setLoading(true);
    setSubmitPhase('creating');
    try {
      const name = businessData.ownerName || email.split('@')[0] || 'User';
      const { role } = getPostSignupDestination(accountSelection);
      const result = await register(name, email, null, role, {
        phone,
        onboardingComplete: false,
        ...(role === USER_ROLES.USED_PENDING ? { authNamespace: 'used' } : {}),
      });
      const user = result?.user || JSON.parse(localStorage.getItem('user') || 'null');
      applySignupSelectionToUser(user, accountSelection, updateUser);
      setSubmitPhase('submitting');
      await finalizePartnerRegistration(user, email, phone);
    } catch (err) {
      const raw = err?.message || 'Registration failed';
      if (/network error|timeout|failed to fetch/i.test(raw)) {
        showAccountError(
          'Network error while submitting. Keep the backend running (port 4001), stay on the https://…:3001 (or :3002) signup page, check Wi‑Fi, then try again.',
          null
        );
      } else if (/email|already registered/i.test(raw)) {
        showAccountError(raw, 'signup-email');
      } else if (/mobile|phone/i.test(raw)) {
        showAccountError(raw, 'signup-phone');
      } else {
        showAccountError(raw, null);
      }
    } finally {
      setLoading(false);
      setSubmitPhase('');
    }
  };

  return (
    <Page className="auth-light-page partner-signup">
      <Shell className="partner-signup-shell">
        <LeftPanel ref={leftPanelRef} className="partner-panel tight-header">
          <LeftInner>
            <Heading id="partner-signup-top" $compact={signupStep > 1}>
              Join India&apos;s <span className="accent">auto</span> marketplace
            </Heading>

            <StepBar>
              <StepItem $active={signupStep === 1} $done={signupStep > 1}>
                <StepDot $active={signupStep === 1} $done={signupStep > 1}>1</StepDot>
                <StepLabel>Account type</StepLabel>
              </StepItem>
              <StepConnector />
              <StepItem $active={signupStep === 2} $done={signupStep > 2}>
                <StepDot $active={signupStep === 2} $done={signupStep > 2}>2</StepDot>
                <StepLabel>Business info</StepLabel>
              </StepItem>
              <StepConnector />
              <StepItem $active={signupStep === 3} $done={signupStep > 3}>
                <StepDot $active={signupStep === 3} $done={signupStep > 3}>3</StepDot>
                <StepLabel>Create account</StepLabel>
              </StepItem>
            </StepBar>

            {signupStep === 1 && (
              <StepPanel $direction={stepDirection} key={`step-1-${accountSelection.mainType || 'pick'}`}>
                <FormScrollBody>
                {localError && <ErrorBox key={localError}>{localError}</ErrorBox>}
                {!accountSelection.mainType && (
                  <>
                    <SectionLabel>Choose account type</SectionLabel>
                    <ConditionGrid>
                      <RoleCard
                        type="button"
                        $square
                        $selected={false}
                        $delay={0}
                        onClick={() => selectMainType('sparepart_seller')}
                      >
                        <RoleIcon $square $selected={false} aria-hidden="true">
                          <FaShoppingBag />
                        </RoleIcon>
                        <RoleTitle $square>Spare part seller</RoleTitle>
                        <GarageCardDesc $square>New &amp; used parts</GarageCardDesc>
                      </RoleCard>
                      <RoleCard
                        type="button"
                        $square
                        $selected={false}
                        $delay={1}
                        onClick={() => selectMainType('garage')}
                      >
                        <RoleIcon $square $selected={false} aria-hidden="true">
                          <FaWrench />
                        </RoleIcon>
                        <RoleTitle $square>Garage</RoleTitle>
                        <GarageCardDesc $square>Scrapyard &amp; workshop</GarageCardDesc>
                      </RoleCard>
                    </ConditionGrid>
                  </>
                )}

                {accountSelection.mainType === 'sparepart_seller' && (
                  <>
                    <SectionLabel>Part condition</SectionLabel>
                    <ConditionGrid>
                      <RoleCard
                        type="button"
                        $square
                        $selected={accountSelection.partCondition === 'new'}
                        $delay={0}
                        onClick={() => selectPartCondition('new')}
                        aria-pressed={accountSelection.partCondition === 'new'}
                      >
                        <RoleIcon $square $selected={accountSelection.partCondition === 'new'} aria-hidden="true">
                          <FaShoppingBag />
                        </RoleIcon>
                        <RoleTitle $square>New parts</RoleTitle>
                      </RoleCard>
                      <RoleCard
                        type="button"
                        $square
                        $selected={accountSelection.partCondition === 'used'}
                        $delay={1}
                        onClick={() => selectPartCondition('used')}
                        aria-pressed={accountSelection.partCondition === 'used'}
                      >
                        <RoleIcon $square $selected={accountSelection.partCondition === 'used'} aria-hidden="true">
                          <FaRecycle />
                        </RoleIcon>
                        <RoleTitle $square>Used parts</RoleTitle>
                      </RoleCard>
                    </ConditionGrid>

                    {accountSelection.partCondition === 'new' && (
                      <>
                        <SectionLabel>Business type</SectionLabel>
                        <RoleGrid>
                          {SELLER_ROLES.map(({ id, label, desc, icon: Icon }, index) => {
                            const selected = isRoleCardSelected(id, accountSelection);
                            return (
                              <RoleCard
                                key={id}
                                type="button"
                                $square
                                $selected={selected}
                                $delay={index}
                                onClick={() => selectRole(id)}
                                aria-pressed={selected}
                                title={desc}
                              >
                                <RoleIcon $square $selected={selected} aria-hidden="true">
                                  <Icon />
                                </RoleIcon>
                                <RoleTitle $square>{label}</RoleTitle>
                              </RoleCard>
                            );
                          })}
                        </RoleGrid>
                      </>
                    )}

                    {accountSelection.partCondition === 'used' && (
                      <>
                        <SectionLabel>Business type</SectionLabel>
                        <UsedSellerGrid>
                          {USED_SELLER_CATEGORIES.map(({ id, label, desc }, index) => {
                            const Icon = USED_SELLER_ROLE_ICONS[id] || FaRecycle;
                            const selected = isUsedSellerCardSelected(id, accountSelection);
                            return (
                              <RoleCard
                                key={id}
                                type="button"
                                $square
                                $tall
                                $selected={selected}
                                $delay={index}
                                onClick={() => selectUsedSellerType(id)}
                                aria-pressed={selected}
                                title={desc}
                              >
                                <RoleIcon $square $selected={selected} aria-hidden="true">
                                  <Icon />
                                </RoleIcon>
                                <RoleTitle $square $tall>{label}</RoleTitle>
                                <GarageCardDesc $square $tall>{desc}</GarageCardDesc>
                              </RoleCard>
                            );
                          })}
                        </UsedSellerGrid>
                      </>
                    )}
                  </>
                )}

                {accountSelection.mainType === 'garage' && (
                  <>
                    <SectionLabel>Garage type</SectionLabel>
                    <GarageGrid>
                      {GARAGE_COMPANY_TYPES.map(({ value, label, desc }, index) => {
                        const Icon = GARAGE_ROLE_ICONS[value] || FaWrench;
                        const selected = isGarageCardSelected(value, accountSelection);
                        return (
                          <RoleCard
                            key={value}
                            type="button"
                            $square
                            $tall
                            $selected={selected}
                            $delay={index}
                            onClick={() => selectGarageType(value)}
                            aria-pressed={selected}
                            title={desc}
                          >
                            <RoleIcon $square $selected={selected} aria-hidden="true">
                              <Icon />
                            </RoleIcon>
                            <RoleTitle $square $tall>{label}</RoleTitle>
                            <GarageCardDesc $square $tall>{desc}</GarageCardDesc>
                          </RoleCard>
                        );
                      })}
                    </GarageGrid>
                  </>
                )}

                </FormScrollBody>
                <FormBtnRow>
                  {showStep1Back && (
                    <FormBackBtn type="button" onClick={handleBackStep1}>
                      ← Back
                    </FormBackBtn>
                  )}
                  <FormPrimaryBtn
                    type="button"
                    onClick={handleContinueStep1}
                    disabled={!hasRoleSelected(accountSelection)}
                  >
                    Continue →
                  </FormPrimaryBtn>
                </FormBtnRow>
              </StepPanel>
            )}

            {signupStep === 2 && (
              <StepPanel $direction={stepDirection} $scrollable key="step-2">
                <PartnerBusinessForm
                  form={businessData}
                  accountMainType={accountSelection.mainType || ''}
                  onChange={(next) => {
                    setBusinessData(next);
                    window.setTimeout(() => saveBusinessDraft(next), 0);
                  }}
                  onBack={handleBackToStep1}
                  onComplete={handleBusinessComplete}
                />
              </StepPanel>
            )}

            {signupStep === 3 && (
              <StepPanel $direction={stepDirection} key="step-3">
                <FormScrollBody>
                {localError && !fieldError.id && <ErrorBox key={localError}>{localError}</ErrorBox>}
                {isSignupSelectionComplete(accountSelection) && (
                  <DetailsForm id="partner-signup-account-form" onSubmit={handleSubmit}>
                    <SectionLabel>Your details</SectionLabel>
                    <StepHint>Mobile number is used to sign in later. Email is stored for updates only.</StepHint>

                    <Field>
                      <FieldLabel htmlFor="signup-email">Email address *</FieldLabel>
                      <InputWrap $hasError={fe('signup-email').$hasError}>
                        <FaEnvelope className="field-icon" />
                        <FieldInput
                          id="signup-email"
                          type="email"
                          name="email"
                          placeholder="you@business.com"
                          value={formData.email}
                          onChange={handleChange}
                          $hasError={fe('signup-email').$hasError}
                          disabled={loading}
                          required
                        />
                      </InputWrap>
                      {fe('signup-email').msg
                        ? <FieldHint $error>{fe('signup-email').msg}</FieldHint>
                        : <FieldHint>We&apos;ll use this for verification updates — not for login</FieldHint>}
                    </Field>

                    <Field>
                      <FieldLabel htmlFor="signup-phone">Mobile number *</FieldLabel>
                      <PhoneRow $hasError={fe('signup-phone').$hasError}>
                        <PhonePrefix>+91</PhonePrefix>
                        <PhoneInput
                          id="signup-phone"
                          type="tel"
                          name="phone"
                          inputMode="numeric"
                          autoComplete="tel-national"
                          placeholder="10-digit mobile number"
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          maxLength={10}
                          disabled={loading}
                          $hasError={fe('signup-phone').$hasError}
                          required
                        />
                      </PhoneRow>
                      {fe('signup-phone').msg
                        ? <FieldHint $error>{fe('signup-phone').msg}</FieldHint>
                        : <FieldHint>You will sign in with this number using OTP</FieldHint>}
                    </Field>

                    <TermsBlock>
                      <TermsLabel id="signup-terms" $hasError={fe('signup-terms').$hasError}>
                        <input
                          type="checkbox"
                          checked={agreeToTerms}
                          onChange={(e) => {
                            setAgreeToTerms(e.target.checked);
                            if (e.target.checked) setFieldError((prev) => (prev.id === 'signup-terms' ? { id: null, message: '' } : prev));
                          }}
                        />
                        <span>
                          I agree to the{' '}
                          <a href="/terms-of-use" target="_blank" rel="noreferrer">Terms of Use</a>
                          {' '}and{' '}
                          <a href="/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>
                        </span>
                      </TermsLabel>
                      {fe('signup-terms').msg && <FieldHint $error>{fe('signup-terms').msg}</FieldHint>}
                    </TermsBlock>
                  </DetailsForm>
                )}
                </FormScrollBody>
                <FormBtnRow>
                  <FormBackBtn type="button" onClick={handleBackToStep2} disabled={loading}>
                    ← Back
                  </FormBackBtn>
                  <FormPrimaryBtn
                    type="submit"
                    form="partner-signup-account-form"
                    disabled={loading || !agreeToTerms}
                  >
                    {loading
                      ? (submitPhase === 'submitting' ? 'Submitting application…' : 'Creating account…')
                      : 'Submit application'}
                  </FormPrimaryBtn>
                </FormBtnRow>
              </StepPanel>
            )}

            <SignInRow>
              Already have an account?{' '}
              <SignInLink type="button" onClick={() => navigate("/login-phone", { state: location.state })}>
                Sign in
              </SignInLink>
            </SignInRow>
          </LeftInner>
        </LeftPanel>

        <PartnerSignupHero />
      </Shell>
    </Page>
  );
};

/* ——— Styled components with CSS variables ——— */

const hideScrollbar = css`
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }
`;

const Page = styled.div`
  ${partnerSignupThemeVars}

  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 20px;
  background: var(--sp-bg-deep, #f8fafc);
  color: var(--pp-text);
  font-family: var(--sp-font, 'Inter', system-ui, sans-serif);
  overflow: hidden;
  overscroll-behavior: none;
  ${hideScrollbar}
  animation: ${fadeIn} 0.4s ease;
  ${reducedMotion}

  html.dark &,
  body.app-dark-theme & {
    background: var(--sp-bg-deep, #060a10);
  }

  @media (max-width: 820px) {
    align-items: flex-start;
    min-height: 100dvh;
    height: auto;
    overflow: visible;
    overscroll-behavior: auto;
    padding: 12px 12px 24px;
  }
`;

const Shell = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  max-width: min(1280px, calc(100vw - 40px));
  max-height: calc(100vh - 32px);
  height: calc(100vh - 32px);
  min-height: 0;
  background: var(--pp-surface);
  border: 1px solid var(--pp-border);
  border-radius: var(--pp-radius-card);
  overflow: hidden;
  animation: ${scaleIn} 0.45s cubic-bezier(0.22, 1, 0.36, 1);
  ${reducedMotion}

  &.partner-signup-shell {
    animation: ${fadeIn} 0.35s ease;
  }

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    min-height: auto;
    max-height: none;
    height: auto;
    max-width: 100%;
    border-radius: 10px;
  }

  @media (max-width: 480px) {
    border-radius: 8px;
    border-left: none;
    border-right: none;
  }
`;

const LeftPanel = styled.div`
  padding: 36px 40px 28px;
  background: var(--pp-surface);
  min-height: 0;
  overflow-y: auto;
  ${hideScrollbar}

  &.partner-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 100%;
    overflow-y: auto;
    ${hideScrollbar}

    &.tight-header {
      @media (min-width: 821px) {
        overflow: hidden;
      }
    }
  }

  &.step-one {
    padding: clamp(16px, 3vw, 22px) clamp(16px, 4vw, 32px) clamp(14px, 2.5vw, 18px);
  }

  &.tight-header {
    padding: 24px 28px 22px;
  }

  @media (max-width: 820px) {
    padding: 24px 20px 20px;
    order: 1;

    &.partner-panel {
      height: auto;
      max-height: none;
      overflow: visible;
    }

    &.step-one {
      padding: 20px 16px 16px;
    }

    &.tight-header {
      padding: 20px 16px 16px;
    }
  }

  @media (max-width: 480px) {
    padding: 20px 16px 16px;

    &.step-one,
    &.tight-header {
      padding: 16px 14px 14px;
    }
  }
`;

const LeftInner = styled.div`
  max-width: 460px;
  margin: 0 auto;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;

  .partner-panel & {
    max-width: 100%;
  }
`;

const LogoBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 28px;
  animation: ${fadeInDown} 0.5s ease both;
  ${reducedMotion}

  .step-one & {
    margin-bottom: 14px;
    flex-shrink: 0;
  }
`;

const LogoImg = styled.img`
  height: 68px;
  width: auto;
  max-width: 200px;
  object-fit: contain;
  flex-shrink: 0;
  display: block;
  animation: ${popIn} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both;
  ${reducedMotion}

  .step-one & {
    height: 56px;
    animation: none;
  }

  @media (max-width: 480px) {
    height: 58px;

    .step-one & {
      height: 48px;
    }
  }
`;

const Heading = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: var(--pp-text);
  margin: 0 0 8px 0;
  line-height: 1.25;
  animation: ${fadeInUp} 0.5s ease 0.12s both;
  ${reducedMotion}
  flex-shrink: 0;

  .accent {
    color: var(--pp-brand);
    display: inline-block;
    animation: ${popIn} 0.4s ease 0.35s both;
    ${reducedMotion}
  }

  @media (max-width: 480px) {
    font-size: 22px;
  }

  .step-one & {
    font-size: 24px;
    margin: 0 0 8px;
    animation: none;
    flex-shrink: 0;
  }

  .tight-header & {
    margin: 0 0 12px;
    flex-shrink: 0;
  }

  ${(p) => p.$compact && css`
    font-size: 18px;
    margin: 0 0 10px;
    animation: none;

    .accent {
      animation: none;
      transform: none;
    }
  `}
`;

const Subheading = styled.p`
  font-size: 14px;
  color: var(--pp-muted);
  margin: 0 0 24px 0;
  line-height: 1.5;
  animation: ${fadeInUp} 0.5s ease 0.2s both;
  ${reducedMotion}

  ${(p) => p.$tight && css`
    margin: 0 0 12px;
    font-size: 13px;
    line-height: 1.4;
    animation: none;
    flex-shrink: 0;
  `}
`;

const StepBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
  animation: ${fadeInUp} 0.5s ease 0.28s both;
  ${reducedMotion}

  .tight-header & {
    margin-bottom: 18px;
    flex-shrink: 0;
    animation: none;
  }

  .step-one & {
    margin-bottom: clamp(10px, 2.5vw, 14px);
    flex-shrink: 0;
    animation: none;
  }

  @media (max-width: 520px) {
    gap: 6px;
    margin-bottom: 16px;

    .tight-header &,
    .step-one & {
      margin-bottom: 10px;
    }
  }
`;

const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => (p.$active ? 'var(--pp-text)' : 'var(--pp-muted)')};

  .step-one & {
    font-size: 12px;
    gap: 6px;
  }
`;

const StepLabel = styled.span`
  @media (max-width: 520px) {
    display: none;
  }
`;

const StepDot = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  background: ${(p) => (p.$active || p.$done ? 'var(--pp-brand)' : 'var(--pp-border)')};
  color: ${(p) => (p.$active || p.$done ? '#fff' : 'var(--pp-muted)')};
  transition: background 0.35s ease, color 0.35s ease;

  ${(p) => p.$active && css`
    box-shadow: 0 0 0 3px var(--pp-brand-bg);
  `}
`;

const StepConnector = styled.div`
  flex: 1;
  max-width: 48px;
  height: 1px;
  background: var(--pp-border);
  transition: background 0.4s ease, transform 0.4s ease;
  transform-origin: left center;

  @media (max-width: 520px) {
    max-width: 24px;
  }
`;

const StepPanel = styled.div`
  ${(p) => (p.$scrollable
    ? css`
      animation: ${fadeIn} 0.3s ease both;
      transform: none;
    `
    : css`
      animation: ${p.$direction === 'back' ? slideInBack : slideInForward} 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
    `)}
  ${reducedMotion}

  ${(p) => p.$scrollable && css`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    @media (max-width: 820px) {
      flex: none;
      min-height: auto;
      overflow: visible;
    }
  `}

  ${(p) => p.$stepOne && css`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    @media (max-width: 820px) {
      flex: none;
      min-height: auto;
      overflow: visible;
    }
  `}
`;

const Step1Body = styled.div`
  min-height: 0;

  ${(p) => p.$fill && css`
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    ${hideScrollbar}

    @media (max-width: 820px) {
      flex: none;
      min-height: auto;
      overflow: visible;
    }
  `}
`;

const FormScrollBody = styled(Step1Body)``;

const FormBtnRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  padding: 16px 0 4px;
  flex-shrink: 0;
  background: var(--pp-surface);
  border-top: 1px solid var(--pp-border);

  ${(p) => p.$stuck && css`
    margin-top: auto;
  `}

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    gap: 8px;
    padding-top: 12px;
  }
`;

const FormBackBtn = styled.button`
  flex: 0 0 auto;
  padding: 12px 16px;
  border: 1px solid var(--pp-border);
  border-radius: var(--pp-radius-btn);
  background: var(--pp-surface);
  color: var(--pp-muted);
  font-size: 14px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    width: 100%;
    text-align: center;
  }
`;

const FormPrimaryBtn = styled.button`
  flex: 1;
  padding: 12px 16px;
  border: none;
  border-radius: var(--pp-radius-btn);
  background: var(--pp-brand);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.92;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const optionGridBase = css`
  display: grid;
  width: 100%;
  gap: clamp(8px, 2vw, 12px);
  margin-bottom: clamp(10px, 2.5vw, 14px);

  .step-one & {
    margin-bottom: clamp(10px, 2vw, 12px);
  }
`;

const StepActions = styled.div`
  margin-top: 4px;
  flex-shrink: 0;
  padding-top: clamp(4px, 1.5vw, 8px);

  @media (max-width: 820px) {
    position: sticky;
    bottom: 0;
    background: linear-gradient(to top, var(--pp-surface) 70%, transparent);
    padding-top: 8px;
    padding-bottom: 2px;
    z-index: 2;
  }
`;

const SectionLabel = styled.p`
  font-size: 11px;
  font-weight: 600;
  color: var(--pp-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 12px 0;
  flex-shrink: 0;

  .tight-header & {
    margin-bottom: 14px;
  }
`;

const ConditionGrid = styled.div`
  ${optionGridBase}
  grid-template-columns: repeat(2, minmax(0, 1fr));
  max-width: min(100%, 320px);

  @media (min-width: 821px) {
    max-width: 280px;
  }

  @media (max-width: 380px) {
    max-width: 100%;
  }
`;

const RoleGrid = styled.div`
  ${optionGridBase}
  grid-template-columns: repeat(4, minmax(0, 1fr));

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const GarageGrid = styled.div`
  ${optionGridBase}
  grid-template-columns: repeat(5, minmax(0, 1fr));

  @media (max-width: 900px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const UsedSellerGrid = styled.div`
  ${optionGridBase}
  grid-template-columns: repeat(3, minmax(0, 1fr));
  width: min(100%, 420px);
  max-width: 420px;
  gap: clamp(8px, 2vw, 12px);

  @media (max-width: 520px) {
    width: min(100%, 360px);
    max-width: 360px;
  }

  @media (max-width: 380px) {
    width: 100%;
    max-width: 100%;
  }
`;

const GarageCardDesc = styled.span`
  font-size: ${(p) => (p.$square ? 'clamp(8px, 2.1vw, 10px)' : '11px')};
  color: var(--pp-muted);
  line-height: 1.3;
  text-align: ${(p) => (p.$square ? 'center' : 'left')};
  overflow-wrap: anywhere;

  ${(p) => !p.$tall && css`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

const RoleCard = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px 4px;
  width: 100%;
  aspect-ratio: ${(p) => (p.$square || p.$wide ? 'auto' : '1')};
  min-height: ${(p) => (p.$wide ? '56px' : 'auto')};
  flex-direction: ${(p) => (p.$wide ? 'row' : 'column')};
  border: 1.5px solid ${(p) => (p.$selected ? 'var(--pp-brand)' : 'var(--pp-border)')};
  border-radius: 8px;
  background: ${(p) => (p.$selected ? 'var(--pp-brand-bg)' : 'var(--pp-surface)')};
  cursor: pointer;
  text-align: center;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
  animation: ${fadeInUp} 0.45s ease ${(p) => 0.08 + p.$delay * 0.1}s both;
  ${reducedMotion}

  ${(p) => p.$square && css`
    flex-direction: column;
    justify-content: center;
    width: 100%;
    height: auto;
    aspect-ratio: 1;
    padding: clamp(12px, 2.5vw, 16px) clamp(8px, 2vw, 12px);
    gap: clamp(6px, 1.5vw, 10px);
    min-height: 0;
    min-width: 0;
    overflow: hidden;
    touch-action: manipulation;
  `}

  ${(p) => p.$tall && css`
    aspect-ratio: auto;
    height: auto;
    min-height: clamp(120px, 26vw, 150px);
    justify-content: flex-start;
    gap: clamp(6px, 1.4vw, 9px);
    padding: clamp(12px, 2.2vw, 15px) clamp(6px, 1.6vw, 10px);
    overflow: visible;
  `}

  .step-one & {
    animation: none;
  }

  &:hover {
    border-color: var(--pp-brand);
    box-shadow: 0 0 0 3px var(--pp-brand-bg);
  }

  ${(p) => p.$selected && css`
    border-color: var(--pp-brand);
    box-shadow: 0 0 0 3px var(--pp-brand-bg);
  `}
`;

const RoleIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(p) => (p.$square ? 'clamp(24px, 7vw, 32px)' : '26px')};
  height: ${(p) => (p.$square ? 'clamp(24px, 7vw, 32px)' : '26px')};
  border-radius: 6px;
  background: ${(p) => (p.$selected ? 'var(--pp-brand)' : 'var(--pp-brand-bg)')};
  color: ${(p) => (p.$selected ? '#fff' : 'var(--pp-brand)')};
  font-size: ${(p) => (p.$square ? 'clamp(11px, 3vw, 14px)' : '12px')};
  flex-shrink: 0;
  transition: background 0.2s ease, color 0.2s ease;
`;

const RoleTitle = styled.span`
  font-size: ${(p) => (p.$square ? 'clamp(9px, 2.4vw, 11px)' : '10px')};
  font-weight: 600;
  color: var(--pp-text);
  line-height: 1.15;
  padding: 0 2px;
  word-break: break-word;
  text-align: ${(p) => (p.$square ? 'center' : 'left')};

  ${(p) => p.$square && !p.$tall && css`
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}

  ${(p) => p.$tall && css`
    overflow: visible;
    white-space: normal;
    overflow-wrap: anywhere;
  `}
`;

const ContinueBtn = styled.button`
  width: 100%;
  padding: 13px 20px;
  background: var(--pp-brand);
  color: #ffffff;
  border: none;
  border-radius: var(--pp-radius-btn);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
  animation: ${fadeInUp} 0.45s ease 0.35s both;
  ${reducedMotion}

  .step-one & {
    padding: 12px 20px;
    font-size: 15px;
    flex-shrink: 0;
    animation: none;
  }

  .arrow {
    display: inline-block;
    transition: transform 0.2s ease;
  }

  &:hover:not(:disabled) {
    opacity: 0.92;
    transform: translateY(-1px);

    .arrow {
      animation: ${arrowNudge} 0.8s ease infinite;
      ${reducedMotion}
    }
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Step1BackLink = styled.button`
  display: block;
  margin-bottom: clamp(8px, 2vw, 12px);
  padding: 4px 0;
  min-height: 44px;
  border: none;
  background: none;
  color: var(--pp-brand);
  font-size: clamp(12px, 3.2vw, 13px);
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  touch-action: manipulation;

  &:hover {
    text-decoration: underline;
  }
`;

const BackLink = styled.button`
  display: block;
  margin-bottom: 16px;
  padding: 0;
  border: none;
  background: none;
  color: var(--pp-brand);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;

const ErrorBox = styled.div`
  background: var(--pp-error-bg);
  color: var(--pp-error-text);
  border: 1px solid var(--pp-error-border);
  border-radius: var(--pp-radius-card);
  padding: 10px 14px;
  font-size: 13px;
  margin-bottom: 16px;
  animation: ${shake} 0.45s ease, ${fadeIn} 0.25s ease;
  ${reducedMotion}
`;

const DetailsForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 0;
`;

const StepHint = styled.p`
  margin: -8px 0 4px;
  font-size: 13px;
  color: var(--pp-muted);
  line-height: 1.45;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: var(--pp-text);
`;

const InputWrap = styled.div`
  position: relative;
  border-radius: var(--pp-radius-btn);

  .field-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: ${(p) => (p.$hasError ? 'var(--pp-error-text)' : 'var(--pp-muted)')};
    font-size: 14px;
    pointer-events: none;
  }
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 11px 12px 11px 38px;
  border: 1px solid ${(p) => (p.$hasError ? 'var(--pp-error-border)' : 'var(--pp-border)')};
  border-radius: var(--pp-radius-btn);
  font-size: 14px;
  color: var(--pp-text);
  background: var(--pp-surface);
  outline: none;
  box-sizing: border-box;

  &.password-input {
    padding-left: 12px;
    padding-right: 40px;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px var(--pp-surface) inset !important;
    box-shadow: 0 0 0 1000px var(--pp-surface) inset !important;
    -webkit-text-fill-color: var(--pp-text) !important;
    caret-color: var(--pp-text);
  }

  &:focus {
    border-color: ${(p) => (p.$hasError ? 'var(--pp-error-text)' : 'var(--pp-brand)')};
    box-shadow: ${(p) => (p.$hasError ? 'none' : '0 0 0 3px var(--pp-brand-bg)')};
  }

  transition: border-color 0.25s ease;

  &::placeholder {
    color: var(--pp-muted);
  }
`;

const FieldHint = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  color: ${(p) => (p.$error ? 'var(--pp-error-text)' : 'var(--pp-muted)')};
`;

const TermsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TermsLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: var(--pp-muted);
  line-height: 1.45;
  cursor: pointer;
  padding: 10px 12px;
  border-radius: var(--pp-radius-btn);
  border: 1px solid ${(p) => (p.$hasError ? 'var(--pp-error-border)' : 'transparent')};
  background: ${(p) => (p.$hasError ? 'var(--pp-error-bg)' : 'transparent')};

  input {
    margin-top: 2px;
    accent-color: var(--pp-brand);
    flex-shrink: 0;
  }

  a {
    color: var(--pp-brand);
    text-decoration: none;
    font-weight: 500;
  }

  a:hover {
    text-decoration: underline;
  }
`;

const OrRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const OrLine = styled.div`
  flex: 1;
  height: 1px;
  background: var(--pp-border);
`;

const OrText = styled.span`
  font-size: 12px;
  color: var(--pp-muted);
`;

const SocialRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`;

const SocialBtn = styled.button`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--pp-border);
  border-radius: var(--pp-radius-btn);
  background: var(--pp-surface);
  cursor: pointer;
  font-size: 18px;
  transition: border-color 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    border-color: var(--pp-brand);
    transform: scale(1.08);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SignInRow = styled.p`
  text-align: center;
  font-size: 14px;
  color: var(--pp-muted);
  margin: 24px 0 0 0;

  .tight-header &,
  .step-one & {
    margin-top: 18px;
    padding-top: 4px;
    flex-shrink: 0;
    font-size: 13px;
  }
`;

const SignInLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: var(--pp-brand);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export default Signup;
